import { ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { Modal, Tag, Typography, notification } from 'antd';
import { ColumnType } from 'antd/lib/table';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useState } from 'react';

import dataOperationClient from 'clients/dataOperation';
import DefaultButton from 'components/atoms/DefaultButton';
import DefaultTable from 'components/composites/DefaultTable';
import { EM_DASH } from 'constants/index';
import { DATE_TIME_FORMAT } from 'constants/time';
import DataOperation, { DataOperationStatus } from 'types/models/data-operation';

const { Text } = Typography;

const STATUS_COLORS: Record<DataOperationStatus, string> = {
  [DataOperationStatus.PENDING]: 'blue',
  [DataOperationStatus.IN_PROGRESS]: 'processing',
  [DataOperationStatus.COMPLETED]: 'success',
  [DataOperationStatus.FAILED]: 'error',
  [DataOperationStatus.RESTORING]: 'warning',
  [DataOperationStatus.RESTORED]: 'default'
};

function RestoreTabContent(): JSX.Element {
  const userTimezone = moment.tz.guess();
  const [operations, setOperations] = useState<DataOperation[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoringUuid, setRestoringUuid] = useState<string | null>(null);

  const fetchOperations = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await dataOperationClient.fetchDataOperations({
        status: DataOperationStatus.COMPLETED
      });

      if (error) {
        Modal.error({
          title: 'Failed to Load Operations',
          content: error.message || 'An error occurred while fetching data operations'
        });
        return;
      }

      if (data) {
        setOperations(data);
      }
    } catch (error) {
      Modal.error({
        title: 'Failed to Load Operations',
        content: 'An unexpected error occurred. Please try again'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOperations();
  }, [fetchOperations]);

  const handleRestore = useCallback((operation: DataOperation) => {
    const { operationUuid, jiraTicketId, parameters } = operation;

    Modal.confirm({
      title: 'Confirm Restore',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Are you sure you want to restore this data transfer operation?</p>
          <p><strong>Operation UUID:</strong> {operationUuid}</p>
          <p><strong>Jira Ticket:</strong> {jiraTicketId}</p>
          <p><strong>Source Sample Point SID:</strong> {parameters.sourceSamplePointSid}</p>
          <p><strong>Target Sample Point SID:</strong> {parameters.targetSamplePointSid}</p>
          <p style={{ marginTop: 16, color: '#ff4d4f' }}>
            <strong>Warning:</strong> This will revert all data changes made by this transfer operation
          </p>
        </div>
      ),
      okText: 'Restore',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setRestoringUuid(operationUuid);
        try {
          const { data, error } = await dataOperationClient.restoreDataTransfer({
            operationUuid
          });

          if (error) {
            Modal.error({
              title: 'Restore Failed',
              content: error.message || 'An error occurred while restoring the data transfer operation'
            });
            return;
          }

          if (data) {
            notification.success({
              message: 'Restore Initiated',
              description: `Operation ${operationUuid} restoration has been initiated and will be processed shortly`,
              duration: 5
            });
            // Refresh the operations list
            fetchOperations();
          }
        } catch (error) {
          Modal.error({
            title: 'Restore Failed',
            content: 'An unexpected error occurred. Please try again'
          });
        } finally {
          setRestoringUuid(null);
        }
      }
    });
  }, [fetchOperations]);

  const columns: ColumnType<DataOperation>[] = [
    {
      title: 'Operation UUID',
      dataIndex: 'operationUuid',
      key: 'operationUuid',
      width: 280,
      render: (uuid: string) => (
        <Text copyable={{ text: uuid }}>
          {uuid}
        </Text>
      )
    },
    {
      title: 'Jira Ticket',
      dataIndex: 'jiraTicketId',
      key: 'jiraTicketId',
      width: 120,
      render: (jiraTicketId: string) => (
        <Text copyable={{ text: jiraTicketId }}>
          {jiraTicketId}
        </Text>
      )
    },
    {
      title: 'Source SID',
      dataIndex: ['parameters', 'sourceSamplePointSid'],
      key: 'sourceSamplePointSid',
      width: 120,
      render: (sourceSamplePointSid: string) => (
        <Text copyable={{ text: sourceSamplePointSid }}>
          {sourceSamplePointSid}
        </Text>
      )
    },
    {
      title: 'Target SID',
      dataIndex: ['parameters', 'targetSamplePointSid'],
      key: 'targetSamplePointSid',
      width: 120,
      render: (targetSamplePointSid: string) => (
        <Text copyable={{ text: targetSamplePointSid }}>
          {targetSamplePointSid}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: DataOperationStatus) => (
        <Tag color={STATUS_COLORS[status]}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Completed At',
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: 180,
      render: (date: number | null) => date
        ? moment(date).tz(userTimezone).format(DATE_TIME_FORMAT.DEFAULT)
        : EM_DASH
    },
    {
      title: 'Restored At',
      dataIndex: 'restoredAt',
      key: 'restoredAt',
      width: 180,
      render: (date: number | null) => date
        ? moment(date).tz(userTimezone).format(DATE_TIME_FORMAT.DEFAULT)
        : EM_DASH
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record: DataOperation) => {
        const isRestoring = restoringUuid === record.operationUuid;

        return (
          <DefaultButton
            type="primary"
            danger
            size="small"
            onClick={() => handleRestore(record)}
            disabled={record.status !== DataOperationStatus.COMPLETED}
            loading={isRestoring}
          >
            Restore
          </DefaultButton>
        );
      }
    }
  ];

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <DefaultButton
          icon={<ReloadOutlined />}
          onClick={fetchOperations}
          loading={loading}
        >
          Refresh
        </DefaultButton>
      </div>
      <DefaultTable<DataOperation>
        columns={columns}
        dataSource={operations}
        rowKey="operationUuid"
        loading={loading}
        empty={
          <Text type="secondary">No completed data transfer operations found</Text>
        }
        scroll={{ x: 1200 }}
      />
    </>
  );
}

export default RestoreTabContent;
